import React, { useState, useEffect, useMemo } from 'react';
import { getVideos, getCategories } from 'utils/methods/safetyguru';
import Breadcrumbs from 'components/Breadcrumbs';
import { useTranslation } from 'react-i18next';

const SafetyGuru = () => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState({
    title: true,
    description: true,
    category: []
  });

  // Fetch videos from backend
  const fetchVideos = async (search = '', category = '') => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (category) params.category = category;
      
      const response = await getVideos(params);
      
      if (response.data.status === 'success') {
        setVideos(response.data.data.videos);
      } else {
        setError(response.data.message || 'Failed to fetch videos');
      }
    } catch (err) {
      setError('Error fetching videos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      
      if (response.data.status === 'success') {
        setCategories(response.data.data.categories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchVideos();
    fetchCategories();
  }, []);

  // Filter videos based on search and filters
  const filteredVideos = useMemo(() => {
    if (!searchQuery) return videos;

    const query = searchQuery.toLowerCase();
    return videos.filter(video => {
      const matchTitle = searchFilters.title && 
        video.title.toLowerCase().includes(query);
      const matchDescription = searchFilters.description && 
        video.description.toLowerCase().includes(query);
      const matchCategory = searchFilters.category.length === 0 || 
        searchFilters.category.includes(video.category);

      return (matchTitle || matchDescription) && matchCategory;
    });
  }, [videos, searchQuery, searchFilters]);

  // Handle search
  const handleSearch = () => {
    // Pass all selected categories as a comma-separated string
    const categoryFilter = searchFilters.category.length > 0 ? searchFilters.category.join(',') : '';
    fetchVideos(searchQuery, categoryFilter);
  };

  // Handle category filter change
  const handleCategoryChange = (category) => {
    const newCategories = searchFilters.category.includes(category)
      ? searchFilters.category.filter(c => c !== category)
      : [...searchFilters.category, category];
    setSearchFilters(prev => ({
      ...prev,
      category: newCategories
    }));
    // Immediately fetch videos for the new category selection
    const categoryFilter = newCategories.length > 0 ? newCategories.join(',') : '';
    fetchVideos(searchQuery, categoryFilter);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('safetyGuru.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => fetchVideos()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            {t('safetyGuru.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-3">
              {t('safetyGuru.title')}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('safetyGuru.subtitle')}
            </p>
            <div className="col-span-5">
              <Breadcrumbs />
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="w-full max-w-xl mx-auto mb-10 bg-white p-6 rounded-lg shadow">
            <div className="flex flex-col space-y-4">
              {/* Search Input */}
              <div className="relative">
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  type="text"
                  placeholder={t('safetyGuru.searchVideos')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={handleSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
              
              {/* Filter Options */}
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                {/* Text Filters */}
                <div className="bg-blue-50 p-3 rounded">
                  <label className="flex items-center space-x-2 cursor-pointer mb-2">
                    <input
                      type="checkbox"
                      checked={searchFilters.title}
                      onChange={(e) => setSearchFilters(prev => ({
                        ...prev,
                        title: e.target.checked
                      }))}
                      className="rounded text-blue-600"
                    />
                    <span className="text-sm text-gray-600">{t('safetyGuru.titles')}</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={searchFilters.description}
                      onChange={(e) => setSearchFilters(prev => ({
                        ...prev,
                        description: e.target.checked
                      }))}
                      className="rounded text-blue-600"
                    />
                    <span className="text-sm text-gray-600">{t('safetyGuru.descriptions')}</span>
                  </label>
                </div>

                {/* Category Filters */}
                <div className="bg-green-50 p-3 rounded">
                  {categories.map(category => (
                    <label key={category} className="flex items-center space-x-2 cursor-pointer mb-1">
                      <input
                        type="checkbox"
                        checked={searchFilters.category.includes(category)}
                        onChange={() => handleCategoryChange(category)}
                        className="rounded text-green-600"
                      />
                      <span className="text-sm text-gray-600">{category}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Videos Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredVideos.length === 0 ? (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-600">No videos found matching your search criteria.</p>
              </div>
            ) : (
              filteredVideos.map((video) => (
                <div
                  key={video.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer"
                  onClick={() => setSelectedVideo(video)}
                >
                  <div className="aspect-w-16 aspect-h-9">
                    <iframe 
                      src={video.embedUrl} 
                      className="w-full h-full"
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 group-hover:text-blue-600 line-clamp-2">
                      {video.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{video.description}</p>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="flex items-center text-gray-500">
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {video.duration}
                      </span>
                      <span className="flex items-center text-gray-500">
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        {video.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Video Modal */}
          {selectedVideo && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedVideo(null)}
            >
              <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden" 
                   onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-col md:flex-row h-full">
                  {/* Video Section */}
                  <div className="w-full md:w-1/2 bg-black">
                    <div className="aspect-w-16 aspect-h-9">
                      <iframe 
                        src={selectedVideo.embedUrl} 
                        className="w-full h-full"
                        frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen
                      />
                    </div>
                  </div>
                  
                  {/* Description Section */}
                  <div className="w-full md:w-1/2 p-6 overflow-y-auto">
                    <div className="flex justify-between items-start mb-4">
                      <h2 className="text-2xl font-bold text-gray-800">{selectedVideo.title}</h2>
                      <button 
                        onClick={() => setSelectedVideo(null)} 
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm mb-4">
                      <span className="flex items-center text-gray-500">
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {selectedVideo.duration}
                      </span>
                      <span className="flex items-center text-gray-500">
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        {selectedVideo.category}
                      </span>
                    </div>

                    <div className="prose max-w-none">
                      <h3 className="text-lg font-semibold mb-2">Overview</h3>
                      <p className="text-gray-600 mb-4">{selectedVideo.description}</p>
                      
                      <h3 className="text-lg font-semibold mb-2">Key Points</h3>
                      <ul className="list-disc pl-4 text-gray-600 space-y-2">
                        {selectedVideo.keyPoints.map((point, index) => (
                          <li key={index}>{point}</li>
                        ))}
                      </ul>

                      <h3 className="text-lg font-semibold mt-4 mb-2">Additional Information</h3>
                      <p className="text-gray-600">{selectedVideo.additionalInfo}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SafetyGuru;
